/**
 * ui/src/components/SimulationDashboard.js
 * ----------------------------------------
 * Trading simulation results dashboard.
 * Displays scenario comparison table and detailed trade breakdowns.
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@mui/material";
import { runSimulation, listScenarios } from "../api/trading";

const SimulationDashboard = () => {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [monthsBack, setMonthsBack] = useState(6);
  const [detailDialog, setDetailDialog] = useState(null); // { scenario_name, trades }

  // Load available scenarios on mount
  React.useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      const data = await listScenarios();
      setScenarios(data.scenarios);
      // Pre-select all scenarios
      setSelectedScenarios(data.scenarios.map((s) => s.id));
    } catch (err) {
      setError(`Failed to load scenarios: ${err.message}`);
    }
  };

  const handleScenarioToggle = (scenarioId) => {
    setSelectedScenarios((prev) =>
      prev.includes(scenarioId)
        ? prev.filter((s) => s !== scenarioId)
        : [...prev, scenarioId]
    );
  };

  const handleRunSimulation = async () => {
    if (selectedScenarios.length === 0) {
      setError("Please select at least one scenario");
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const data = await runSimulation(selectedScenarios, monthsBack);
      setResults(data.simulations);
    } catch (err) {
      setError(`Simulation failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (result) => {
    setDetailDialog({
      scenario_name: result.scenario_name,
      trades: result.trades,
    });
  };

  const handleCloseDetail = () => {
    setDetailDialog(null);
  };

  // Color for P&L chips
  const getPnLColor = (value) => {
    if (value > 0) return "success";
    if (value < 0) return "error";
    return "default";
  };

  // Color for metric chips
  const getMetricColor = (value, goodThreshold) => {
    return value >= goodThreshold ? "success" : "warning";
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Trading Simulation Dashboard
      </Typography>

      {error && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "#ffebee",
            color: "#c62828",
            borderRadius: 1,
          }}
        >
          {error}
        </Box>
      )}

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select Scenarios
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {scenarios.map((scenario) => (
                  <Box key={scenario.id} sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={selectedScenarios.includes(scenario.id)}
                      onChange={() => handleScenarioToggle(scenario.id)}
                    />
                    <Typography variant="body2">{scenario.name}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box>
              <TextField
                label="Months Back"
                type="number"
                value={monthsBack}
                onChange={(e) => setMonthsBack(Math.max(1, parseInt(e.target.value, 10)))}
                sx={{ width: 150 }}
                inputProps={{ min: 1, max: 60 }}
              />
            </Box>

            <Box>
              <Button
                variant="contained"
                onClick={handleRunSimulation}
                disabled={loading || selectedScenarios.length === 0}
              >
                {loading ? <CircularProgress size={24} /> : "Run Simulation"}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Results Table */}
      {results.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>Scenario</TableCell>
                <TableCell align="right">Initial Capital</TableCell>
                <TableCell align="right">Total Trades</TableCell>
                <TableCell align="right">Closed Trades</TableCell>
                <TableCell align="right">Win Rate %</TableCell>
                <TableCell align="right">Avg Win %</TableCell>
                <TableCell align="right">Avg Loss %</TableCell>
                <TableCell align="right">Total Return %</TableCell>
                <TableCell align="right">Max Drawdown %</TableCell>
                <TableCell align="right">Sharpe Ratio</TableCell>
                <TableCell align="center">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((result) => (
                <TableRow key={result.scenario_name}>
                  <TableCell>{result.scenario_name}</TableCell>
                  <TableCell align="right">${result.initial_capital.toLocaleString()}</TableCell>
                  <TableCell align="right">{result.total_trades}</TableCell>
                  <TableCell align="right">{result.closed_trades}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${result.win_rate_pct}%`}
                      color={getMetricColor(result.win_rate_pct, 50)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${result.avg_win_pct}%`}
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${result.avg_loss_pct}%`}
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${result.total_return_pct}%`}
                      color={getPnLColor(result.total_return_pct)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${result.max_drawdown_pct}%`}
                      color={result.max_drawdown_pct > 20 ? "error" : "warning"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={result.sharpe_ratio.toFixed(2)}
                      color={getMetricColor(result.sharpe_ratio, 1.0)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleOpenDetail(result)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      {detailDialog && (
        <Dialog open={!!detailDialog} onClose={handleCloseDetail} maxWidth="lg" fullWidth>
          <DialogTitle>
            Trades for {detailDialog.scenario_name}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Entry Date</TableCell>
                    <TableCell align="right">Entry Price</TableCell>
                    <TableCell align="right">Shares</TableCell>
                    <TableCell align="right">Exit Date</TableCell>
                    <TableCell align="right">Exit Price</TableCell>
                    <TableCell align="right">Exit Reason</TableCell>
                    <TableCell align="right">Realised P&L $</TableCell>
                    <TableCell align="right">Realised P&L %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailDialog.trades.map((trade) => (
                    <TableRow key={trade.id}>
                      <TableCell>{trade.symbol}</TableCell>
                      <TableCell align="right">{trade.entry_date}</TableCell>
                      <TableCell align="right">${trade.entry_price.toFixed(2)}</TableCell>
                      <TableCell align="right">{trade.shares}</TableCell>
                      <TableCell align="right">{trade.exit_date || "—"}</TableCell>
                      <TableCell align="right">
                        {trade.exit_price ? `$${trade.exit_price.toFixed(2)}` : "—"}
                      </TableCell>
                      <TableCell align="right">{trade.exit_reason || "—"}</TableCell>
                      <TableCell align="right">
                        {trade.realised_pnl_usd !== null && trade.realised_pnl_usd !== undefined ? (
                          <Chip
                            label={`$${trade.realised_pnl_usd.toFixed(2)}`}
                            color={getPnLColor(trade.realised_pnl_usd)}
                            size="small"
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {trade.realised_pnl_pct !== null && trade.realised_pnl_pct !== undefined ? (
                          <Chip
                            label={`${trade.realised_pnl_pct.toFixed(2)}%`}
                            color={getPnLColor(trade.realised_pnl_pct)}
                            size="small"
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetail}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default SimulationDashboard;
